<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { UserAddIcon } from "@heroicons/vue/solid";
import type { PropType } from "vue";
import type { Collection } from "~/types/store";

const props = defineProps({
  modelValue: { type: Boolean as PropType<boolean>, required: true },
  profileId: { type: String, required: true },
});
const emit = defineEmits(['update:modelValue', 'onSave']);

const profileId = toRef(props, 'profileId');

const collection = reactive<Collection>({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  iconUrl: '',
  bannerUrl: '',
  attributes: [],
});

watch(
  () => props.modelValue,
  (newShow, oldShow) => {
    if (newShow && !oldShow) {
      Object.assign(collection, {
        id: crypto.randomUUID(),
        name: '',
        description: '',
        iconUrl: '',
        bannerUrl: '',
        attributes: [],
      });
    }
  }
);

const saveAndClose = () => {
  emit('onSave', collection);
  emit('update:modelValue', false);
};
</script>
<template>
  <TransitionRoot as="template" :show="props.modelValue">
    <Dialog as="div" class="relative z-50" @close="emit('update:modelValue', false)">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="bg-opacity-75 fixed inset-0 bg-neutral-500 transition-opacity dark:bg-neutral-400/70" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all dark:bg-black sm:my-8 sm:w-full sm:max-w-xl sm:p-6"
            >
              <div>
                <div class="sm:flex sm:items-start">
                  <div
                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                  >
                    <UserAddIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 flex flex-1 flex-col gap-2 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" class="text-lg font-medium leading-6 text-neutral-900 dark:text-neutral-50">
                      Create collection
                    </DialogTitle>
                    <GobyCreateCollectionForm :profile-id="profileId" :model-value="collection" />
                  </div>
                </div>
                <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    class="inline-flex w-full justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-emerald-700 dark:text-white sm:ml-3 sm:w-auto sm:text-sm"
                    @click="saveAndClose"
                  >
                    Save collection
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
